import { Color3, Vector3 } from '@babylonjs/core';
import { Asset } from './Asset';
import { PatternConfig } from './LevelConfig';

/**
 * Configuration for asset objects.
 * Defines the assets to be used, the asset objects and the distribution configurations.
 * Assets are the raw assets to be used.
 * Asset objects are the objects to be spawned, they define the asset to be used and the distribution configuration.
 * Distribution configurations define how often and where the asset objects will be spawned.
 */
export interface AssetBasedConfig<T extends DistributionConfig> {
  /**
   * Assets to be used for this configuration
   */
  assets: Asset[];

  /**
   * Asset objects to be used for this configuration
   */
  assetObjects: AssetObject[];

  /**
   * Distribution configurations - one per type/placement rule
   */
  distributions: T[];

  /**
   * Range from player at which entities will spawn
   */
  spawnRange?: number;

  /**
   * Range from player beyond which entities will despawn
   */
  despawnRange?: number;
}

/**
 * Distribution configuration for all object types.
 * Enables distribution of entities per interval (structured) or probability (random).
 */
export interface DistributionConfig {
  /**
   * Asset types to use
   */
  types?: string[];

  /**
   * Path point interval for generation/spawning entities
   */
  interval?: number;

  /**
   * Probability of generation/spawning an entity, alternative to interval
   */
  probability?: number;

  /**
   * If > 0 the probability of generation/spawning an entity increases along the path
   * The value is multiplied with the path point index and added to the probability for each path point
   */
  progressionProbability?: number;

  /**
   * Minimum path point index from where entities will spawn
   */
  minPoint?: number;

  /**
   * Maximum path point index from where entities will spawn
   */
  maxPoint?: number;

  /**
   * Skip probability (chance to skip generation/spawning an entity even if the interval or probability is met)
   */
  skipProbability?: number;

  /**
   * Lanes to spawn entities on
   *
   * A lane index is : 0 is center, -1 is left, 1 is right, etc.
   * Therefore the width of a lane is computed and multiplied with this lane index to offset the obstacle.
   */
  lanes?: number[];

  /**
   * A pattern leads to a list of relative positions relative to the computed spawn point.
   * This allows to spawn multiple entities at the same path point forming different patterns.
   */
  pattern?: PatternConfig;
}

export interface SpawnPoint<T extends DistributionConfig> {
  id: string;
  position: Vector3;
  pathIndex: number;
  distribution: T;
  assetObject: AssetObject;
  spawned: boolean;
  spawnRange?: number;
  despawnRange?: number;
  lateralOffset?: number;
}

export class AssetObject {
  constructor(
    public asset: string,
    public name: string = undefined,
    public scale: number = 1,
    public centerOrigin: boolean = false,
    public clone: boolean = false, // clone mesh, no animations or skeletions
    public instance: boolean = false,
    public type: string = undefined,
    public deepClone: boolean = false, // clones mesh including animations and skeletions
    public computeCenter: boolean = false,
    public metallic?: number,
    public roughness?: number,
    public diffuseColor?: Color3
  ) {}

  static fromAsset(asset: string, scale: number = 1): AssetObject {
    return new AssetObject(asset, undefined, scale, true);
  }

  withMetallic(metallic: number): AssetObject {
    this.metallic = metallic;
    return this;
  }

  withScale(scale: number): AssetObject {
    this.scale = scale;
    return this;
  }

  withRoughness(roughness: number): AssetObject {
    this.roughness = roughness;
    return this;
  }

  withDiffuseColor(diffuseColor: Color3): AssetObject {
    this.diffuseColor = diffuseColor;
    return this;
  }

  withMaterialProperties(metallic: number, roughness: number, diffuseColor: Color3): AssetObject {
    this.metallic = metallic;
    this.roughness = roughness;
    this.diffuseColor = diffuseColor;
    return this;
  }

  withCenterOrigin(centerOrigin: boolean): AssetObject {
    this.centerOrigin = centerOrigin;
    return this;
  }

  withComputeCenter(computeCenter: boolean): AssetObject {
    this.computeCenter = computeCenter;
    return this;
  }

  withClone(clone: boolean): AssetObject {
    this.clone = clone;
    return this;
  }

  withDeepClone(deepClone: boolean): AssetObject {
    this.deepClone = deepClone;
    return this;
  }

  withInstance(instance: boolean): AssetObject {
    this.instance = instance;
    return this;
  }

  withType(type: string): AssetObject {
    this.type = type;
    return this;
  }
}
